// Projects on this device, with React subscriptions. Every edit is written to IndexedDB at
// once, so closing the app or losing power never loses a measurement.

import { useSyncExternalStore } from "react";

import { db } from "../lib/db";
import type { PhotoRef, Project } from "./model";

let projects = new Map<string, Project>();
let loaded = false;
const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version += 1;
  for (const l of listeners) l();
}

export async function loadProjects(): Promise<void> {
  const all = await db.all<Project>("projects");
  projects = new Map(all.map((p) => [p.id, p]));
  loaded = true;
  emit();
}

export function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function getProject(id: string): Project | undefined {
  return projects.get(id);
}

export function listProjects(owner: { org_id: string; user_id: string }): Project[] {
  return [...projects.values()]
    .filter((p) => p.org_id === owner.org_id && p.user_id === owner.user_id)
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
}

export async function saveProject(p: Project): Promise<void> {
  projects.set(p.id, p);
  emit();
  await db.put("projects", p);
}

/** Change a project: the mutator edits a copy, which replaces the stored one. */
export async function updateProject(id: string, mutate: (p: Project) => void, opts: { edit?: boolean } = {}): Promise<Project> {
  const current = projects.get(id);
  if (!current) throw new Error(`no project ${id}`);
  const next = structuredClone(current);
  mutate(next);
  next.updated_at = new Date().toISOString();
  // Bookkeeping (send results) is not an edit: it must not make a sent survey look changed.
  if (opts.edit !== false) next.edited_at = next.updated_at;
  await saveProject(next);
  return next;
}

export async function deleteProject(id: string): Promise<void> {
  const p = projects.get(id);
  projects.delete(id);
  emit();
  await db.delete("projects", id);
  for (const photo of p?.photos ?? []) await db.delete("blobs", photoKey(photo));
}

export function photoKey(photo: Pick<PhotoRef, "sha256">): string {
  return `photo:${photo.sha256}`;
}

export async function photoBlob(photo: Pick<PhotoRef, "sha256">): Promise<Blob | undefined> {
  return db.get<Blob>("blobs", photoKey(photo));
}

export function useProjectsVersion(): number {
  return useSyncExternalStore(subscribe, () => version);
}

export function useProject(id: string | undefined): Project | undefined {
  useProjectsVersion();
  return id ? projects.get(id) : undefined;
}

export function projectsLoaded(): boolean {
  return loaded;
}
